import {
  makeStyles,
  TableBody,
  TableCell,
  TableFooter,
  TableRow
} from "@material-ui/core";
import Checkbox from "@saleor/components/Checkbox";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TableHead from "@saleor/components/TableHead";
import TablePagination from "@saleor/components/TablePagination";
import { CoverList_covers_edges_node } from "@saleor/cover/types/CoverList";
import { CoverListUrlSortField } from "@saleor/cover/urls";
import { maybe, renderCollection } from "@saleor/misc";
import { ListActions, ListProps, SortPage } from "@saleor/types";
import { getArrowDirection } from "@saleor/utils/sort";
import moment from "moment-timezone";
import React from "react";
import { FormattedMessage } from "react-intl";

export interface CoverListProps
  extends ListProps,
    ListActions,
    SortPage<CoverListUrlSortField> {
  cover: CoverList_covers_edges_node[];
}
const numberOfColumns = 4;

const useStyles = makeStyles(
  theme => ({
    [theme.breakpoints.up("lg")]: {
      apply: {
        width: 80
      },
      colName: {
        width: "auto",
        textAlign: "left",
        overflow: "hidden", // 超出的文本隐藏
        textOverflow: "ellipsis", // 溢出用省略号显示
        whiteSpace: "nowrap" // 溢出不换行
      }
    },
    apply: {
      color: "red"
    }
  }),
  { name: "CoverList" }
);
const CoverList: React.FC<CoverListProps> = ({
  cover,
  disabled,
  isChecked,
  onNextPage,
  onPreviousPage,
  onRowClick,
  pageInfo,
  selected,
  sort,
  toggle,
  toggleAll,
  toolbar,
  onSort,
  settings,
  onUpdateListSettings
}) => {
  const classes = useStyles();
  return (
    <>
      <ResponsiveTable>
        <TableHead
          colSpan={numberOfColumns}
          selected={selected}
          disabled={disabled}
          items={cover}
          toggleAll={toggleAll}
          toolbar={toolbar}
        >
          <TableCellHeader
            direction={
              sort.sort === CoverListUrlSortField.name
                ? getArrowDirection(sort.asc)
                : undefined
            }
            className={classes.colName}
            arrowPosition="right"
            onClick={() => onSort(CoverListUrlSortField.name)}
          >
            <FormattedMessage defaultMessage="Cover Name" />
          </TableCellHeader>
          <TableCellHeader
            direction={
              sort.sort === CoverListUrlSortField.title
                ? getArrowDirection(sort.asc)
                : undefined
            }
            className={classes.colName}
            arrowPosition="right"
            onClick={() => onSort(CoverListUrlSortField.title)}
          >
            <FormattedMessage defaultMessage="Cover Title" />
          </TableCellHeader>
          <TableCellHeader
            direction={
              sort.sort === CoverListUrlSortField.subtitle
                ? getArrowDirection(sort.asc)
                : undefined
            }
            className={classes.colName}
            arrowPosition="right"
            onClick={() => onSort(CoverListUrlSortField.subtitle)}
          >
            <FormattedMessage defaultMessage="Cover Subtitle" />
          </TableCellHeader>
          <TableCellHeader
            direction={
              sort.sort === CoverListUrlSortField.createDate
                ? getArrowDirection(sort.asc)
                : undefined
            }
            className={classes.colName}
            arrowPosition="right"
            onClick={() => onSort(CoverListUrlSortField.createDate)}
          >
            <FormattedMessage defaultMessage="Create Date" />
          </TableCellHeader>
        </TableHead>
        <TableFooter>
          <TableRow>
            <TablePagination
              colSpan={numberOfColumns}
              settings={settings}
              hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
              onNextPage={onNextPage}
              onUpdateListSettings={onUpdateListSettings}
              hasPreviousPage={
                pageInfo && !disabled ? pageInfo.hasPreviousPage : false
              }
              onPreviousPage={onPreviousPage}
            />
          </TableRow>
        </TableFooter>
        <TableBody>
          {renderCollection(
            cover,
            cover => {
              const isSelected = cover ? isChecked(cover.id) : false;
              return (
                <TableRow
                  hover={!!cover}
                  onClick={cover && onRowClick(cover.id)}
                  key={cover ? cover.id : "skeleton"}
                  selected={isSelected}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isSelected}
                      disabled={disabled}
                      disableClickPropagation
                      onChange={() => toggle(cover.id)}
                    />
                  </TableCell>

                  <TableCell className={classes.colName}>
                    {maybe<React.ReactNode>(() => cover.name, <Skeleton />)}
                  </TableCell>
                  <TableCell className={classes.colName} data-test="id">
                    {maybe<React.ReactNode>(() => cover.title, <Skeleton />)}
                  </TableCell>

                  <TableCell className={classes.colName} data-test="name">
                    {maybe<React.ReactNode>(() => cover.subtitle, <Skeleton />)}
                  </TableCell>

                  <TableCell className={classes.colName}>
                    {cover ? (
                      moment(cover.createDate).format("YYYY-MM-DD HH:mm:ss")
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                </TableRow>
              );
            },
            () => (
              <TableRow data-test="emptyListMessage">
                <TableCell colSpan={numberOfColumns}>
                  <FormattedMessage defaultMessage="No cover found" />
                </TableCell>
              </TableRow>
            )
          )}
        </TableBody>
      </ResponsiveTable>
    </>
  );
};

export default CoverList;
